import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents'
import { DecoratorBlockNode, type SerializedDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode'
import * as React from 'react'
import parse from 'html-react-parser'
import DOMPurify from 'dompurify'
import type { EditorConfig, LexicalEditor, NodeKey, Spread } from 'lexical'

export type SerializedFigmaNode = Spread<
  {
    documentID: string;
  },
  SerializedDecoratorBlockNode
>;

export const isValidIframe = (html: string): string | false => {
  const cleanHTML = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['iframe'],
    ALLOWED_ATTR: ['src', 'width', 'height', 'title', 'sandbox', 'allow']
  })

  // Additional manual checks
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = cleanHTML

  const iframe = tempDiv.querySelector('iframe')
  if (!iframe) return false

  const src = iframe.getAttribute('src')
  if (!src) return false

  try {
    const url = new URL(src)
    if (url.protocol !== 'https:') {
      return false
    }
  } catch (e) {
    return false
  }

  return tempDiv.innerHTML
}

function FigmaComponent({ className, format, nodeKey, documentID }: {
  className: { base: string, focus: string };
  format: any;
  nodeKey: NodeKey;
  documentID: string;
}): React.JSX.Element {
  return (
    <BlockWithAlignableContents className={className} format={format} nodeKey={nodeKey}>
      {parse(documentID) as any}
    </BlockWithAlignableContents>
  )
}

export class FigmaNode extends DecoratorBlockNode {
  __id: string

  static getType(): string {
    return 'figma'
  }

  static clone(node: FigmaNode): FigmaNode {
    return new FigmaNode(node.__id, node.__format, node.__key)
  }

  static importJSON(serializedNode: SerializedFigmaNode): FigmaNode {
    const node = $createFigmaNode(serializedNode.documentID)
    node.setFormat(serializedNode.format)
    return node
  }

  exportJSON(): SerializedFigmaNode {
    return {
      ...super.exportJSON(),
      documentID: this.__id,
      type: 'figma',
      version: 1
    }
  }

  constructor(id: string, format?: any, key?: NodeKey) {
    super(format, key)
    this.__id = id
  }

  updateDOM(): false {
    return false
  }

  getId(): string {
    return this.__id
  }

  getTextContent(_includeInert?: boolean, _includeDirectionless?: boolean): string {
    return `https://www.figma.com/file/${this.__id}`
  }

  decorate(_editor: LexicalEditor, config: EditorConfig): React.JSX.Element {
    const embedBlockTheme = config.theme.embedBlock || {}
    const className = {
      base: (embedBlockTheme.base as string) || '',
      focus: (embedBlockTheme.focus as string) || ''
    }
    return (
      <FigmaComponent
        className={className}
        format={this.__format}
        nodeKey={this.getKey()}
        documentID={this.__id}
      />
    )
  }
}

export function $createFigmaNode(documentID: string): FigmaNode {
  return new FigmaNode(documentID)
}

export function $isFigmaNode(node: any): node is FigmaNode {
  return node instanceof FigmaNode
}
